import * as s from "../VideoShareStyled";
import { TableTemplate } from "components/templates/default/table/TableTemplate";
import { ReactComponent as VIDEO_SHARE_ICON } from "assets/icons/videoshare_white.svg";
import { MemoVideoPlayer } from "components/blocks/video/VideoPlayer";
import {
  HashtagInterface,
  VideoShareViewResult,
} from "api/interfaces/videoshareInterface.interface";
import ViewVSContent from "./ViewVSContent";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete";
import ViewVSUserList from "./ViewVSUserList";
import { useRef } from "react";

import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useMenu } from "hook/useMenu";
import RVSMWrapper from "./RVSMWrapper";
import TableButton from "components/atoms/buttons/TableButton";
import { ReactComponent as TRASH_ICON } from "assets/icons/trash.svg";
import Hashtag from "components/blocks/hashtag/Hashtag";
import { toTrim } from "utils/functions";

type Props = {
  videoId: string | undefined;
  videoUrl: string;
  state: VideoShareViewResult | undefined;
  isLoading: boolean;
  isDelete: boolean;
  isDisabledAdd: boolean;
  playerReady: (player: any) => void;
  onDelete: (value: boolean) => void;
  onConfirmDelete: () => void;
  onUpdateContentValues: (key: string, value: any) => void;
  selectedAccount: ProfileAccountInfo;
  videoTimezone: string;
  tags: string[];
  setTags: (tags: string[]) => void;
  isEditTags: boolean;
  onToggleEditTags: () => void;
  onSaveTags: () => void;
};

export default function ViewVSPresenter({
  videoId,
  videoUrl,
  state,
  isLoading,
  isDelete,
  isDisabledAdd,
  playerReady,
  onDelete,
  onConfirmDelete,
  onUpdateContentValues,
  selectedAccount,
  videoTimezone,
  tags,
  setTags,
  isEditTags,
  onToggleEditTags,
  onSaveTags,
}: Props): JSX.Element {
  const ref = useRef<any>();
  const { menuNavigation } = useMenu({});

  const titleComponent = [
    {
      label: (
        <s.TitleComponent>
          <p className="icon">
            <VIDEO_SHARE_ICON />
          </p>
          Video Share
        </s.TitleComponent>
      ),

      onClick: () => menuNavigation({ to: "/videoShare" }),
    },
    {
      label: state?.title ? state.title : "Undefined",
    },
  ];

  // setting the video-js option for the player
  const videoJsOptions = {
    autoplay: false,
    controls: true,
    fill: true,
    crossorigin: "anonymous",
    muted: true,
    controlBar: {
      pictureInPictureToggle: false,
    },
    hls: {
      overrideNative: true,
    },
    html5: {
      vhs: {
        withCredentials: true,
      },
    },
    // sources: [
    //   {
    //     src: videoUrl,
    //     // type: "application/x-mpegURL",
    //   },
    // ],
  };
  return (
    <>
      {!isLoading &&
        state !== undefined &&
        state.title !== "" &&
        videoId !== undefined && (
          <>
            <TableTemplate titleComponent={titleComponent} noSearch>
              <s.ViewInner>
                <MemoVideoPlayer
                  title={state.title}
                  videoUrl={state.url}
                  videoResult={state}
                  videoId={videoId}
                  options={videoJsOptions}
                  onReady={playerReady}
                  isAuth={true}
                  timezone={state.timeZone}
                  isDisabledAdd={isDisabledAdd}
                  onClickShare={() => {
                    if (ref.current !== undefined) {
                      ref.current.openSharePopup();
                    }
                  }}
                  uploadStatus={state.uploadStatus}
                  videoTimezone={videoTimezone}
                />

                <ViewVSContent videoId={videoId} state={state} />
              </s.ViewInner>
              <s.BottomWrapper>
                <s.BottomTagWrapper>
                  {isEditTags ? (
                    <s.HashtagInputWrapper>
                      <Hashtag
                        defaultValue={tags.join(",")}
                        maxTagLength={20}
                        onChange={(e) => {
                          setTags(
                            e.detail.tagify.value.map((tag: any) => tag.value)
                          );
                        }}
                        onKeydown={(
                          event: CustomEvent<
                            Tagify.KeydownEventData<Tagify.TagData>
                          >
                        ) => {
                          const keyboardEvent = event.detail.event;
                          if (keyboardEvent.key === "Escape") {
                            onSaveTags();
                          }
                        }}
                      />

                      <RVSMWrapper>
                        <s.TextButton
                          className="cancel"
                          onClick={onToggleEditTags}
                          data-testid="videoshare-view-view-text-button-toggle-edit-tags"
                        >
                          CANCEL
                        </s.TextButton>
                        <s.TextButton
                          className="edit"
                          onClick={onSaveTags}
                          data-testid="videoshare-view-view-text-button-save-tags"
                        >
                          SAVE
                        </s.TextButton>
                      </RVSMWrapper>
                    </s.HashtagInputWrapper>
                  ) : (
                    <s.TagWrapper>
                      <s.TagInner>
                        {(state.tags || []).map(
                          (tag: HashtagInterface, idx: number) => (
                            <s.ViewHashtag key={idx} isFilter={false}>
                              {`#${toTrim(tag.tag)}`}
                            </s.ViewHashtag>
                          )
                        )}
                      </s.TagInner>
                      <RVSMWrapper>
                        <s.TextButton
                          className="edit"
                          onClick={onToggleEditTags}
                          data-testid="videoshare-view-view-text-button-toggle-edit-tags-2"
                        >
                          {(state.tags ?? []).length > 0
                            ? "EDIT TAGS"
                            : "ADD TAGS"}
                        </s.TextButton>
                      </RVSMWrapper>
                    </s.TagWrapper>
                  )}
                </s.BottomTagWrapper>

                <RVSMWrapper>
                  <s.Footer>
                    <TableButton
                      label="Delete Video"
                      buttonType="warning"
                      onClick={() => onDelete(true)}
                      icon={<TRASH_ICON style={{ marginLeft: 5 }} />}
                      data-testid="videoshare-view-view-table-button-delete"
                    />
                  </s.Footer>
                </RVSMWrapper>
              </s.BottomWrapper>
              <ViewVSUserList
                state={state}
                videoId={videoId}
                isDisabledAdd={isDisabledAdd}
                ref={ref}
              />
            </TableTemplate>
          </>
        )}

      {isDelete && (
        <DeleteConfirmModal
          onDelete={() => onDelete(false)}
          onConfirmDelete={onConfirmDelete}
        />
      )}
    </>
  );
}
